/**
 * Aggregation utilities for grouping metric data by week, month, or year
 */

export type AggregationType = "none" | "weekly" | "monthly" | "yearly";

export interface AggregationPeriod {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  missingDates: string[];
  daysWithData: number;
  daysWithoutData: number;
  coveragePct: number;
  minValue: number | null;
  maxValue: number | null;
  meanValue: number | null;
  label: string; // e.g., "Week of Jan 1, 2025" or "January 2025"
}

/**
 * Check if the date range is valid for the selected aggregation type
 */
export function isValidAggregationRange(
  fromDate: string,
  endDate: string,
  aggregationType: AggregationType,
): boolean {
  if (aggregationType === "none") return true;

  const start = new Date(`${fromDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  const diffDays = Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (aggregationType === "weekly") {
    return diffDays > 14; // More than 2 weeks
  } else if (aggregationType === "monthly") {
    return diffDays >= 60; // At least 2 months (roughly)
  } else if (aggregationType === "yearly") {
    return diffDays >= 365; // At least 1 year
  }

  return false;
}

/**
 * Get the minimum required days for each aggregation type
 */
export function getMinDaysForAggregation(
  aggregationType: AggregationType,
): number {
  switch (aggregationType) {
    case "weekly":
      return 15; // More than 2 weeks
    case "monthly":
      return 60; // At least 2 months
    case "yearly":
      return 365; // At least 1 year
    default:
      return 0;
  }
}

/**
 * Compute aggregation periods from raw metric data
 */
export function computeAggregations(
  fromDate: string,
  endDate: string,
  missingDates: string[],
  allData: { date: string; value: number }[],
): AggregationPeriod[] {
  const missingSet = new Set(missingDates);
  const periods: AggregationPeriod[] = [];

  const start = new Date(`${fromDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);

  // Group data into periods
  let periodStart = new Date(start);
  let periodEnd: Date;
  let periodNum = 0;

  while (periodStart < end) {
    periodNum++;

    // Calculate period end date
    const tempDate = new Date(periodStart);
    tempDate.setUTCDate(tempDate.getUTCDate() + 7); // Default to weekly
    periodEnd = new Date(Math.min(tempDate.getTime(), end.getTime()));

    // Collect data for this period
    const periodMissingDates: string[] = [];
    let daysWithData = 0;
    let values: number[] = [];

    // Generate all dates in this period
    const currentDate = new Date(periodStart);
    while (currentDate <= periodEnd && currentDate <= end) {
      const dateStr = currentDate.toISOString().slice(0, 10);
      if (missingSet.has(dateStr)) {
        periodMissingDates.push(dateStr);
      } else {
        daysWithData++;
        const dataPoint = allData.find((d) => d.date === dateStr);
        if (dataPoint) {
          values.push(dataPoint.value);
        }
      }
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    const totalDays =
      Math.ceil(
        (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;
    const daysWithoutData = totalDays - daysWithData;
    const coveragePct = totalDays > 0 ? (daysWithData / totalDays) * 100 : 0;

    const periodEndStr = periodEnd.toISOString().slice(0, 10);
    const periodStartStr = periodStart.toISOString().slice(0, 10);

    periods.push({
      startDate: periodStartStr,
      endDate: periodEndStr,
      missingDates: periodMissingDates,
      daysWithData,
      daysWithoutData,
      coveragePct,
      minValue: values.length > 0 ? Math.min(...values) : null,
      maxValue: values.length > 0 ? Math.max(...values) : null,
      meanValue:
        values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : null,
      label: `Week ${periodNum}`,
    });

    periodStart = new Date(periodEnd);
    periodStart.setUTCDate(periodStart.getUTCDate() + 1);
  }

  return periods;
}

/**
 * Compute weekly aggregations
 */
export function computeWeeklyAggregations(
  fromDate: string,
  endDate: string,
  missingDates: string[],
  allData?: Array<{ date: string; value: number }>,
): { periods: AggregationPeriod[]; totalDays: number } {
  const missingSet = new Set(missingDates);
  const dataMap = allData
    ? new Map(allData.map((d) => [d.date, d.value]))
    : new Map();
  const periods: AggregationPeriod[] = [];

  const start = new Date(`${fromDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  const totalDays =
    Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  // Start from the beginning of the week containing fromDate
  const periodStart = new Date(start);
  const dayOfWeek = periodStart.getUTCDay();
  periodStart.setUTCDate(periodStart.getUTCDate() - dayOfWeek);

  let currentStart = new Date(periodStart);
  let weekNum = 0;

  while (currentStart <= end) {
    weekNum++;
    const currentEnd = new Date(currentStart);
    currentEnd.setUTCDate(currentEnd.getUTCDate() + 6);

    const actualEnd = new Date(Math.min(currentEnd.getTime(), end.getTime()));

    const periodMissingDates: string[] = [];
    let daysWithData = 0;
    const values: number[] = [];

    const loopDate = new Date(currentStart);
    while (loopDate <= actualEnd) {
      const dateStr = loopDate.toISOString().slice(0, 10);
      if (dateStr >= fromDate && dateStr <= endDate) {
        if (missingSet.has(dateStr)) {
          periodMissingDates.push(dateStr);
        } else {
          daysWithData++;
          if (dataMap.has(dateStr)) {
            values.push(dataMap.get(dateStr)!);
          }
        }
      } else if (dateStr > endDate) {
        break;
      }
      loopDate.setUTCDate(loopDate.getUTCDate() + 1);
    }

    const periodDays =
      Math.ceil(
        (actualEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;
    const daysWithoutData = periodDays - daysWithData;
    const coveragePct = periodDays > 0 ? (daysWithData / periodDays) * 100 : 0;

    periods.push({
      startDate: currentStart.toISOString().slice(0, 10),
      endDate: actualEnd.toISOString().slice(0, 10),
      missingDates: periodMissingDates,
      daysWithData,
      daysWithoutData,
      coveragePct,
      minValue: values.length > 0 ? Math.min(...values) : null,
      maxValue: values.length > 0 ? Math.max(...values) : null,
      meanValue:
        values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : null,
      label: `Week ${weekNum} (${currentStart.toISOString().slice(0, 10)})`,
    });

    currentStart.setUTCDate(currentStart.getUTCDate() + 7);
  }

  return { periods, totalDays };
}

/**
 * Compute monthly aggregations
 */
export function computeMonthlyAggregations(
  fromDate: string,
  endDate: string,
  missingDates: string[],
  allData?: Array<{ date: string; value: number }>,
): { periods: AggregationPeriod[]; totalDays: number } {
  const missingSet = new Set(missingDates);
  const dataMap = allData
    ? new Map(allData.map((d) => [d.date, d.value]))
    : new Map();
  const periods: AggregationPeriod[] = [];

  const start = new Date(`${fromDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  const totalDays =
    Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  let currentYear = start.getUTCFullYear();
  let currentMonth = start.getUTCMonth();
  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth();

  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    const monthStart = new Date(Date.UTC(currentYear, currentMonth, 1));
    const monthEnd = new Date(Date.UTC(currentYear, currentMonth + 1, 0));

    const periodStart = new Date(
      Math.max(monthStart.getTime(), start.getTime()),
    );
    const periodEnd = new Date(Math.min(monthEnd.getTime(), end.getTime()));

    const periodMissingDates: string[] = [];
    let daysWithData = 0;
    const values: number[] = [];

    const loopDate = new Date(periodStart);
    while (loopDate <= periodEnd) {
      const dateStr = loopDate.toISOString().slice(0, 10);
      if (missingSet.has(dateStr)) {
        periodMissingDates.push(dateStr);
      } else {
        daysWithData++;
        if (dataMap.has(dateStr)) {
          values.push(dataMap.get(dateStr)!);
        }
      }
      loopDate.setUTCDate(loopDate.getUTCDate() + 1);
    }

    const periodDays =
      Math.ceil(
        (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;
    const daysWithoutData = periodDays - daysWithData;
    const coveragePct = periodDays > 0 ? (daysWithData / periodDays) * 100 : 0;

    const monthName = monthStart.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });

    periods.push({
      startDate: periodStart.toISOString().slice(0, 10),
      endDate: periodEnd.toISOString().slice(0, 10),
      missingDates: periodMissingDates,
      daysWithData,
      daysWithoutData,
      coveragePct,
      minValue: values.length > 0 ? Math.min(...values) : null,
      maxValue: values.length > 0 ? Math.max(...values) : null,
      meanValue:
        values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : null,
      label: monthName,
    });

    currentMonth++;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    }
  }

  return { periods, totalDays };
}

/**
 * Compute yearly aggregations
 */
export function computeYearlyAggregations(
  fromDate: string,
  endDate: string,
  missingDates: string[],
  allData?: Array<{ date: string; value: number }>,
): { periods: AggregationPeriod[]; totalDays: number } {
  const missingSet = new Set(missingDates);
  const dataMap = allData
    ? new Map(allData.map((d) => [d.date, d.value]))
    : new Map();
  const periods: AggregationPeriod[] = [];

  const start = new Date(`${fromDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  const totalDays =
    Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  let currentYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();

  while (currentYear <= endYear) {
    const yearStart = new Date(Date.UTC(currentYear, 0, 1));
    const yearEnd = new Date(Date.UTC(currentYear, 11, 31));

    const periodStart = new Date(
      Math.max(yearStart.getTime(), start.getTime()),
    );
    const periodEnd = new Date(Math.min(yearEnd.getTime(), end.getTime()));

    const periodMissingDates: string[] = [];
    let daysWithData = 0;
    const values: number[] = [];

    const loopDate = new Date(periodStart);
    while (loopDate <= periodEnd) {
      const dateStr = loopDate.toISOString().slice(0, 10);
      if (missingSet.has(dateStr)) {
        periodMissingDates.push(dateStr);
      } else {
        daysWithData++;
        if (dataMap.has(dateStr)) {
          values.push(dataMap.get(dateStr)!);
        }
      }
      loopDate.setUTCDate(loopDate.getUTCDate() + 1);
    }

    const periodDays =
      Math.ceil(
        (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;
    const daysWithoutData = periodDays - daysWithData;
    const coveragePct = periodDays > 0 ? (daysWithData / periodDays) * 100 : 0;

    periods.push({
      startDate: periodStart.toISOString().slice(0, 10),
      endDate: periodEnd.toISOString().slice(0, 10),
      missingDates: periodMissingDates,
      daysWithData,
      daysWithoutData,
      coveragePct,
      minValue: values.length > 0 ? Math.min(...values) : null,
      maxValue: values.length > 0 ? Math.max(...values) : null,
      meanValue:
        values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : null,
      label: `Year ${currentYear}`,
    });

    currentYear++;
  }

  return { periods, totalDays };
}
